import { createContext, useContext, type ReactNode } from "react";

import { ChevronRight } from "./icons";
import { cn } from "./cn";

/**
 * Design system rule: one Card, no cards inside cards. Nesting is not merely
 * discouraged — a nested Card drops its own surface and renders as a plain
 * group, so the rule cannot be broken by accident from a distant call site.
 */
const InsideCard = createContext(false);

export interface CardProps {
  children: ReactNode;
  className?: string;
  /** Optional heading rendered above the card body. */
  title?: ReactNode;
  variant?: "default" | "system-ability" | "summary";
  elevated?: boolean;
  /** No inner padding: rows and dividers run edge to edge, the title is inset. */
  flush?: boolean;
}

export function Card({
  children,
  className,
  title,
  variant = "default",
  elevated = false,
  flush = false,
}: CardProps) {
  const nested = useContext(InsideCard);
  const body = (
    <>
      {title !== undefined && (
        <div
          className={cn(
            "text-headline text-fg",
            flush ? "px-4 pb-1.5 pt-3.5" : "mb-3",
          )}
        >
          {title}
        </div>
      )}
      {children}
    </>
  );
  if (nested) return <div className={cn("min-w-0", className)}>{body}</div>;
  return (
    <InsideCard.Provider value={true}>
      <div
        data-variant={variant}
        className={cn(
          "relative min-w-0 rounded-xl bg-raised shadow-card",
          flush ? "p-0" : "p-4",
          variant === "system-ability" && "pr-8",
          variant === "summary" && "bg-sunken shadow-none",
          elevated && "shadow-sheet",
          className,
        )}
      >
        {variant === "system-ability" && (
          <span
            aria-hidden="true"
            className="absolute right-3 top-3 size-2 rounded-full bg-system"
          />
        )}
        {body}
      </div>
    </InsideCard.Provider>
  );
}

export interface ListItemProps {
  primary: ReactNode;
  secondary?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  /** Accessible name when the row is actionable. */
  label?: string;
}

/** A flat row with a divider — never a card inside a card. */
export function ListItem({
  primary,
  secondary,
  leading,
  trailing,
  selected = false,
  onClick,
  label,
}: ListItemProps) {
  const content = (
    <>
      {leading && <span className="shrink-0">{leading}</span>}
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate text-body font-semibold text-fg">
          {primary}
        </span>
        {secondary !== undefined && (
          <span className="block truncate text-footnote text-accent-fg">
            {secondary}
          </span>
        )}
      </span>
      {trailing ?? (onClick && <ChevronRight size={14} />)}
    </>
  );
  const className = cn(
    "flex w-full min-h-11 items-center gap-3 border-b border-line-subtle px-1 py-2 last:border-b-0",
    selected ? "bg-accent-subtle" : "bg-transparent",
    onClick &&
      "cursor-pointer hover:bg-hover-overlay active:bg-active-overlay focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
  );
  if (!onClick) return <div className={className}>{content}</div>;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={className}
    >
      {content}
    </button>
  );
}

export function Divider({ className }: { className?: string }) {
  return (
    <hr
      className={cn("my-3 border-0 border-t border-line-subtle", className)}
    />
  );
}
